import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus, BarChart3, Play, FileText } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { TestSetList } from '@/components/evaluation/TestSetList'
import { CreateTestSetModal } from '@/components/evaluation/CreateTestSetModal'
import { useEvaluationStats } from '@/hooks/useEvaluationStats'
import { PROTECTED_ROUTES } from '@/constants/routes'

const EvaluationPage: React.FC = () => {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const { stats, loading: statsLoading } = useEvaluationStats()

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">RAG Evaluation System</h1>
          <p className="text-muted-foreground">
            Manage test sets and evaluate the performance of your RAG system
          </p>
        </div>
        <div className="flex gap-2">
          <Link to={PROTECTED_ROUTES.EVALUATION_ANALYTICS}>
            <Button variant="outline" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              Analytics
            </Button>
          </Link>
          <Button onClick={() => setShowCreateModal(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            New Test Set
          </Button>
        </div>
      </div>

      {/* Quick Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Test Sets</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? (
                <div className="h-8 w-8 animate-pulse bg-muted rounded" />
              ) : (
                stats?.totalTestSets ?? 0
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Active evaluation test sets
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Recent Runs</CardTitle>
            <Play className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? (
                <div className="h-8 w-8 animate-pulse bg-muted rounded" />
              ) : (
                stats?.recentActivity?.length ?? 0
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Evaluations in last 30 days
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Score</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? (
                <div className="h-8 w-8 animate-pulse bg-muted rounded" />
              ) : (
                stats?.averageScore !== undefined ? stats.averageScore.toFixed(1) : 'N/A'
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Overall performance score
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="test-sets" className="space-y-4">
        <TabsList>
          <TabsTrigger value="test-sets">Test Sets</TabsTrigger>
          <TabsTrigger value="recent-runs">Recent Runs</TabsTrigger>
        </TabsList>
        
        <TabsContent value="test-sets" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Test Sets</CardTitle>
              <CardDescription>
                Manage your evaluation test sets and run evaluations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TestSetList />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="recent-runs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Evaluation Runs</CardTitle>
              <CardDescription>
                View recent evaluation runs and their results
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                Recent runs will be displayed here
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Test Set Modal */}
      <CreateTestSetModal 
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />
    </div>
  )
}

export default EvaluationPage